import { useEffect, useState } from 'react';
import { CheckCircle2, Circle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { supabase } from '@/integrations/supabase/client';

interface ProgressItem {
  id: string;
  label: string;
  completed: boolean;
}

interface FounderProgressChecklistProps {
  companyId: string;
}

export function FounderProgressChecklist({ companyId }: FounderProgressChecklistProps) {
  const [items, setItems] = useState<ProgressItem[]>([
    { id: 'company', label: 'Company created', completed: true },
    { id: 'valuation', label: 'Initial valuation generated', completed: false },
    { id: 'founder', label: 'Founder ownership added', completed: false },
    { id: 'round', label: 'Funding round configured', completed: false },
    { id: 'register', label: 'Share register initialized', completed: false },
  ]);

  useEffect(() => {
    async function check() {
      const [{ count: shareholderCount }, { count: ledgerCount }, { count: roundCount }] =
        await Promise.all([
          supabase
            .from('shareholders')
            .select('*', { count: 'exact', head: true })
            .eq('company_id', companyId),
          supabase
            .from('ledger_events')
            .select('*', { count: 'exact', head: true })
            .eq('company_id', companyId),
          supabase
            .from('funding_rounds')
            .select('*', { count: 'exact', head: true })
            .eq('company_id', companyId),
        ]);

      setItems((prev) =>
        prev.map((item) => {
          if (item.id === 'valuation') return { ...item, completed: true }; // generated during onboarding
          if (item.id === 'founder') return { ...item, completed: (shareholderCount || 0) > 0 };
          if (item.id === 'round') return { ...item, completed: (roundCount || 0) > 0 };
          if (item.id === 'register') return { ...item, completed: (ledgerCount || 0) > 0 };
          return item;
        })
      );
    }
    check();
  }, [companyId]);

  const completed = items.filter((i) => i.completed).length;
  const progress = (completed / items.length) * 100;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg font-medium">Setup progress</CardTitle>
        <div className="flex items-center gap-3 mt-2">
          <Progress value={progress} className="h-2 flex-1" />
          <span className="text-xs text-muted-foreground tabular-nums">
            {completed}/{items.length}
          </span>
        </div>
      </CardHeader>
      <CardContent className="space-y-2 pt-0">
        {items.map((item) => (
          <div key={item.id} className="flex items-center gap-3 py-1.5">
            {item.completed ? (
              <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0" />
            ) : (
              <Circle className="h-4 w-4 text-muted-foreground/40 flex-shrink-0" />
            )}
            <span
              className={`text-sm ${item.completed ? 'text-muted-foreground line-through' : ''}`}
            >
              {item.label}
            </span>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
